import React, { useCallback, useEffect, useRef, useState } from "react";

import metadata from "../../common/metadata.json";
import "./UpdateAvailableBanner.scss";

/**
 * UpdateAvailableBanner (Change #100012).
 *
 * A released build only reaches an open tab when the user refreshes, and
 * nothing told them one was out. generate-buildno.js now publishes the build
 * number as /build-version.json alongside the deployed app; this polls it and,
 * when the deployed revision is newer than the one this tab was compiled with,
 * shows a banner offering a refresh.
 *
 * A static file rather than a field on the token renewal: it is served by the
 * same deploy as the bundle, so it can only ever say "new" once the new build is
 * really live, and it needs no backend change.
 *
 * Checks: shortly after load, every few minutes, and whenever the tab comes back
 * into view (throttled) - a tab left open overnight is the usual case.
 * "Later" hides it for that revision only; the next release shows it again.
 */

const FIRST_CHECK_MS = 30 * 1000;
const POLL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
const DISMISS_KEY = "update_banner_dismissed_revision";

const RUNNING_REVISION = Number(metadata?.buildRevision) || 0;

// sessionStorage can throw (privacy modes, blocked storage) - never let it break the app.
const readDismissed = () => {
  try {
    return Number(sessionStorage.getItem(DISMISS_KEY)) || 0;
  } catch (e) {
    return 0;
  }
};
const writeDismissed = (revision) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(revision));
  } catch (e) {
    // ignore
  }
};

const UpdateAvailableBanner = () => {
  const [latest, setLatest] = useState(null); // { buildMajor, buildMinor, buildRevision, buildDate }
  const [dismissed, setDismissed] = useState(readDismissed);
  const lastCheckRef = useRef(0);

  const check = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckRef.current < MIN_GAP_MS) return;
    lastCheckRef.current = now;
    try {
      const response = await fetch(`/build-version.json?t=${now}`, {
        cache: "no-store",
      });
      if (!response.ok) return;
      // The dev server answers unknown paths with index.html; that fails to
      // parse and is simply ignored.
      const data = await response.json();
      if (Number(data?.buildRevision) > RUNNING_REVISION) setLatest(data);
    } catch (e) {
      // Offline, or no file on this deploy yet - try again next time.
    }
  }, []);

  useEffect(() => {
    if (!RUNNING_REVISION) return undefined;
    const first = setTimeout(check, FIRST_CHECK_MS);
    const timer = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  const revision = Number(latest?.buildRevision) || 0;
  if (!revision || revision <= dismissed) return null;

  const label = `${latest.buildMajor ?? metadata.buildMajor}.${
    latest.buildMinor ?? metadata.buildMinor
  }.${revision}`;

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <i className="ri-refresh-line update-banner__icon" />
      <span className="update-banner__text">
        A new version of CRMit is available <strong>({label})</strong>. Refresh
        to get it.
      </span>
      <button
        type="button"
        className="update-banner__refresh"
        onClick={() => window.location.reload()}
      >
        Refresh now
      </button>
      <button
        type="button"
        className="update-banner__later"
        onClick={() => {
          writeDismissed(revision);
          setDismissed(revision);
        }}
      >
        Later
      </button>
    </div>
  );
};

export default UpdateAvailableBanner;
